import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta, type PageRequest } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import {
  assignmentListSchema,
  classSubjectListSchema,
  collectorListSchema,
  instituteSubjectListSchema,
  memberInstituteListSchema,
  paperListSchema,
  monitorDetailResponseSchema,
  monitorResponseSchema,
  questionClassListSchema,
  questionProjectResponseSchema,
  questionSubjectResponseSchema,
  uploadSubjectListSchema,
  type Assignment,
  type AssignedSubjectPayload,
  type ClassSubject,
  type Collector,
  type CreateAssignmentPayload,
  type InstituteSubject,
  type InstituteSubjectQuery,
  type MemberInstitute,
  type Paper,
  type MonitorDetailResponse,
  type MonitorResponse,
  type MonitorSearch,
  type ProjectSearch,
  type QuestionClass,
  type QuestionClassPayload,
  type QuestionProject,
  type QuestionProjectPayload,
  type QuestionProjectResponse,
  type QuestionSubjectPayload,
  type QuestionSubjectResponse,
  type SubjectListSearch,
  type UploadSubject,
} from '../types/question.types';
import {
  DECLARED_PROJECTS_PAGE_SIZE,
  DEFAULT_QUESTION_PAGE_SIZE,
  type MonitorGrouping,
} from '../constants/question.constants';

const SINGLE_PROJECT_PAGE_SIZE = 1;

export const questionQueryKeys = {
  all: () => ['question'] as const,
  monitor: (params: MonitorSearch, groupBy: MonitorGrouping) =>
    [...questionQueryKeys.all(), 'monitor', groupBy, params] as const,
  monitorDetail: (params: MonitorSearch & { anyProjectState?: boolean }) =>
    [...questionQueryKeys.all(), 'monitor-detail', params] as const,
  projects: (params: ProjectSearch & PageRequest) =>
    [...questionQueryKeys.all(), 'projects', params] as const,
  projectList: (params: ProjectSearch) =>
    [...questionQueryKeys.all(), 'projects', 'list', params] as const,
  project: (projectId: number | undefined) =>
    [...questionQueryKeys.all(), 'projects', 'detail', projectId] as const,
  instituteSubjects: (projectInstituteId: number, query: InstituteSubjectQuery) =>
    [...questionQueryKeys.all(), 'institute-subjects', projectInstituteId, query] as const,
  classes: () => [...questionQueryKeys.all(), 'classes'] as const,
  subjects: (params: SubjectListSearch) =>
    [...questionQueryKeys.all(), 'subjects', params] as const,
  classSubjects: (classId: number | undefined, isActive: boolean | undefined) =>
    [...questionQueryKeys.all(), 'class-subjects', classId, isActive] as const,
  assignments: (projectId: number, userId: number | undefined) =>
    [...questionQueryKeys.all(), 'assignments', projectId, userId] as const,
  myAssignments: (projectId: number | undefined) =>
    [...questionQueryKeys.all(), 'my-assignments', projectId] as const,
  myProjects: () => [...questionQueryKeys.all(), 'my-projects'] as const,
  assignmentSubjects: (projectInstituteId: number | undefined) =>
    [...questionQueryKeys.all(), 'assignment-subjects', projectInstituteId] as const,
  papers: (instituteSubjectId: number | undefined) =>
    [...questionQueryKeys.all(), 'papers', instituteSubjectId] as const,
  collectors: (search: string | undefined, boardId: number | undefined) =>
    [...questionQueryKeys.all(), 'collectors', search, boardId] as const,
  memberInstitutes: (userId: number | undefined, search: string | undefined) =>
    [...questionQueryKeys.all(), 'member-institutes', userId, search] as const,
};

export const monitorInfiniteQueryOptions = (params: MonitorSearch, groupBy: MonitorGrouping) =>
  infiniteQueryOptions({
    queryKey: questionQueryKeys.monitor(params, groupBy),
    queryFn: async ({ pageParam }): Promise<MonitorResponse> => {
      const res = await apiClient.get(API.question.downloadMonitor, {
        params: { ...params, groupBy, page: pageParam, pageSize: DEFAULT_QUESTION_PAGE_SIZE },
      });
      return monitorResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const monitorDetailInfiniteQueryOptions = (params: MonitorSearch, anyProjectState = false) =>
  infiniteQueryOptions({
    queryKey: questionQueryKeys.monitorDetail({ ...params, anyProjectState }),
    queryFn: async ({ pageParam }): Promise<MonitorDetailResponse> => {
      const res = await apiClient.get(API.question.monitorDetail, {
        params: {
          ...params,
          ...(anyProjectState ? { anyProjectState: true } : {}),
          page: pageParam,
          pageSize: DEFAULT_QUESTION_PAGE_SIZE,
        },
      });
      return monitorDetailResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const questionProjectsQueryOptions = (params: ProjectSearch & PageRequest) =>
  queryOptions({
    queryKey: questionQueryKeys.projects(params),
    queryFn: async (): Promise<QuestionProjectResponse> => {
      const res = await apiClient.get(API.question.projects, { params });
      return questionProjectResponseSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const questionProjectQueryOptions = (projectId: number | undefined) =>
  queryOptions({
    queryKey: questionQueryKeys.project(projectId),
    queryFn: async (): Promise<QuestionProject | null> => {
      const res = await apiClient.get(API.question.projects, {
        params: { projectId, page: FIRST_PAGE, pageSize: SINGLE_PROJECT_PAGE_SIZE },
      });
      return questionProjectResponseSchema.parse(res.data).data[0] ?? null;
    },
    enabled: projectId !== undefined,
    staleTime: STALE_TIME,
  });

export const questionProjectsInfiniteQueryOptions = (params: ProjectSearch) =>
  infiniteQueryOptions({
    queryKey: questionQueryKeys.projectList(params),
    queryFn: async ({ pageParam }): Promise<QuestionProjectResponse> => {
      const res = await apiClient.get(API.question.projects, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_QUESTION_PAGE_SIZE },
      });
      return questionProjectResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const myQuestionProjectsInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: questionQueryKeys.myProjects(),
    queryFn: async ({ pageParam }): Promise<QuestionProjectResponse> => {
      const res = await apiClient.get(API.question.myProjects, {
        params: { page: pageParam, pageSize: DECLARED_PROJECTS_PAGE_SIZE },
      });
      return questionProjectResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const instituteSubjectsQueryOptions = (
  projectInstituteId: number | null,
  query: InstituteSubjectQuery = {},
) =>
  queryOptions({
    queryKey: questionQueryKeys.instituteSubjects(projectInstituteId ?? 0, query),
    queryFn: async (): Promise<InstituteSubject[]> => {
      const res = await apiClient.get(API.question.instituteSubjects(projectInstituteId ?? 0), {
        params: query,
      });
      return instituteSubjectListSchema.parse(res.data);
    },
    enabled: projectInstituteId !== null,
    staleTime: STALE_TIME,
  });

export const questionClassesQueryOptions = () =>
  queryOptions({
    queryKey: questionQueryKeys.classes(),
    queryFn: async (): Promise<QuestionClass[]> => {
      const res = await apiClient.get(API.question.classes);
      return questionClassListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const questionSubjectsInfiniteQueryOptions = (params: SubjectListSearch) =>
  infiniteQueryOptions({
    queryKey: questionQueryKeys.subjects(params),
    queryFn: async ({ pageParam }): Promise<QuestionSubjectResponse> => {
      const res = await apiClient.get(API.question.subjects, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_QUESTION_PAGE_SIZE },
      });
      return questionSubjectResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const classSubjectsQueryOptions = (classId: number | undefined, isActive?: boolean) =>
  queryOptions({
    queryKey: questionQueryKeys.classSubjects(classId, isActive),
    queryFn: async (): Promise<ClassSubject[]> => {
      const res = await apiClient.get(API.question.classSubjects, {
        params: {
          ...(classId === undefined ? {} : { classId }),
          ...(isActive === undefined ? {} : { isActive }),
        },
      });
      return classSubjectListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function createQuestionClass(payload: QuestionClassPayload): Promise<void> {
  await apiClient.post(API.question.classes, payload);
}

export async function updateQuestionClass(
  id: number,
  payload: QuestionClassPayload,
): Promise<void> {
  await apiClient.put(API.question.classById(id), payload);
}

export async function deleteQuestionClass(id: number): Promise<void> {
  await apiClient.delete(API.question.classById(id));
}

export async function createQuestionSubject(payload: QuestionSubjectPayload): Promise<void> {
  await apiClient.post(API.question.subjects, payload);
}

export async function updateQuestionSubject(
  id: number,
  payload: QuestionSubjectPayload,
): Promise<void> {
  await apiClient.put(API.question.subjectById(id), payload);
}

export async function deleteQuestionSubject(id: number): Promise<void> {
  await apiClient.delete(API.question.subjectById(id));
}

export async function assignSubject(classId: number, subjectId: number): Promise<void> {
  await apiClient.post(API.question.classSubjects, { classId, subjectId });
}

export async function unassignSubject(classSubjectId: number): Promise<void> {
  await apiClient.delete(API.question.classSubjectById(classSubjectId));
}

export const assignmentsQueryOptions = (
  projectId: number | undefined,
  userId: number | undefined,
) =>
  queryOptions({
    queryKey: questionQueryKeys.assignments(projectId ?? 0, userId),
    queryFn: async (): Promise<Assignment[]> => {
      const res = await apiClient.get(API.question.assignments(projectId ?? 0), {
        params: { userId },
      });
      return assignmentListSchema.parse(res.data);
    },
    enabled: projectId !== undefined && userId !== undefined,
    staleTime: STALE_TIME,
  });

export const myAssignmentsQueryOptions = (projectId: number | undefined) =>
  queryOptions({
    queryKey: questionQueryKeys.myAssignments(projectId),
    queryFn: async (): Promise<Assignment[]> => {
      const res = await apiClient.get(API.question.myAssignments, {
        params: { projectId },
      });
      return assignmentListSchema.parse(res.data);
    },
    enabled: projectId !== undefined,
    staleTime: STALE_TIME,
  });

export const assignmentSubjectsQueryOptions = (projectInstituteId: number | undefined) =>
  queryOptions({
    queryKey: questionQueryKeys.assignmentSubjects(projectInstituteId),
    queryFn: async (): Promise<UploadSubject[]> => {
      const res = await apiClient.get(API.question.assignmentSubjects(projectInstituteId ?? 0));
      return uploadSubjectListSchema.parse(res.data);
    },
    enabled: projectInstituteId !== undefined,
    staleTime: STALE_TIME,
  });

export const papersQueryOptions = (instituteSubjectId: number | undefined) =>
  queryOptions({
    queryKey: questionQueryKeys.papers(instituteSubjectId),
    queryFn: async (): Promise<Paper[]> => {
      const res = await apiClient.get(API.question.papers(instituteSubjectId ?? 0));
      return paperListSchema.parse(res.data);
    },
    enabled: instituteSubjectId !== undefined,
    staleTime: STALE_TIME,
  });

export async function createQuestionProject(payload: QuestionProjectPayload): Promise<void> {
  await apiClient.post(API.question.projects, payload);
}

export async function updateQuestionProject(
  id: number,
  payload: QuestionProjectPayload,
): Promise<void> {
  await apiClient.put(API.question.projectById(id), payload);
}

export async function deleteQuestionProject(id: number): Promise<void> {
  await apiClient.delete(API.question.projectById(id));
}

export async function createAssignment(
  projectId: number,
  payload: CreateAssignmentPayload,
): Promise<void> {
  await apiClient.post(API.question.assignments(projectId), payload);
}

export async function updateAssignedSubjects(
  projectInstituteId: number,
  subjects: AssignedSubjectPayload[],
): Promise<void> {
  await apiClient.put(API.question.assignmentSubjects(projectInstituteId), {
    subjects,
  });
}

export async function removeAssignment(projectInstituteId: number): Promise<void> {
  await apiClient.delete(API.question.assignmentById(projectInstituteId));
}

export async function uploadPaper(instituteSubjectId: number, file: File): Promise<void> {
  const body = new FormData();
  body.append('file', file);
  await apiClient.post(API.question.papers(instituteSubjectId), body, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

export async function resetPapers(instituteSubjectId: number): Promise<void> {
  await apiClient.delete(API.question.papers(instituteSubjectId));
}

export async function downloadPaper(paperId: number, fileName: string): Promise<void> {
  const res = await apiClient.get(API.question.paperFile(paperId), {
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, fileName);
}

export const memberInstitutesQueryOptions = (userId: number | undefined, search?: string) =>
  queryOptions({
    queryKey: questionQueryKeys.memberInstitutes(userId, search),
    queryFn: async (): Promise<MemberInstitute[]> => {
      const res = await apiClient.get(API.question.memberInstitutes(userId ?? 0), {
        params: search === undefined || search === '' ? {} : { search },
      });
      return memberInstituteListSchema.parse(res.data);
    },
    enabled: userId !== undefined,
    staleTime: STALE_TIME,
  });

export const collectorsQueryOptions = (search?: string, boardId?: number) =>
  queryOptions({
    queryKey: questionQueryKeys.collectors(search, boardId),
    queryFn: async (): Promise<Collector[]> => {
      const res = await apiClient.get(API.question.collectors, {
        params: {
          ...(search === undefined || search === '' ? {} : { search }),
          ...(boardId === undefined ? {} : { boardId }),
        },
      });
      return collectorListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });
